/**
 * Session state. `/me` is the only source of identity; a 401 means guest, which
 * is a valid state (Browse is the guest surface) rather than an error to render.
 */

import { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { apiFetch } from '../api/client';
import { routes } from '../api/routes';
import { ProblemError } from '../api/problem';
import type { Me } from '../api/types';

interface SessionValue {
  me: Me | null;
  loading: boolean;
  authenticated: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue>({
  me: null,
  loading: true,
  authenticated: false,
  refresh: async () => {},
  signOut: async () => {},
});

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const next = await apiFetch<Me>(routes.me.profile);
      setMe(next);
    } catch (e) {
      // 401 is the guest state, not a failure. Anything else leaves the previous
      // identity in place rather than logging the user out on a blip.
      if (e instanceof ProblemError && e.status === 401) setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await apiFetch(routes.auth.logout, { method: 'POST' });
    } finally {
      setMe(null);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<SessionValue>(
    () => ({ me, loading, authenticated: me !== null, refresh, signOut }),
    [me, loading, refresh, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  return useContext(SessionContext);
}
